import { UFS } from '../domain/entities/content.entity';
import type { Institution } from '../domain/entities/content.entity';

/**
 * Os filtros da busca de questões (DESEN-42/43), como ficam na URL: texto
 * vazio é "sem filtro". Servem às duas telas, Buscar questões e Gestão de
 * comentários. Duas cadeias independentes:
 *
 * - **o assunto**: Grande Área → Especialidade → Tema (pai, filho e neto, nos
 *   nomes do Ruan). Trocar um nível limpa os de baixo, e o `subjectId` que vai
 *   ao back é o do nível mais fundo escolhido (`deepestSubjectId`);
 * - **a prova**: UF → Instituição, como na busca de provas. Trocar a UF limpa
 *   a instituição.
 *
 * O Ano não depende de nada: as opções dele são as das provas reais.
 */
export interface QuestionSearchFilters {
  area: string;
  specialty: string;
  theme: string;
  uf: string;
  institutionId: string;
  year: string;
}

export const EMPTY_QUESTION_FILTERS: QuestionSearchFilters = {
  area: '',
  specialty: '',
  theme: '',
  uf: '',
  institutionId: '',
  year: '',
};

export function hasQuestionFilter(f: QuestionSearchFilters): boolean {
  return Object.values(f).some((v) => v !== '');
}

/** O assunto que vai ao back: o do nível mais fundo, que traz os de baixo. */
export function deepestSubjectId(f: QuestionSearchFilters): string {
  return f.theme || f.specialty || f.area;
}

export function changeArea(
  f: QuestionSearchFilters,
  area: string,
): QuestionSearchFilters {
  return { ...f, area, specialty: '', theme: '' };
}

export function changeSpecialty(
  f: QuestionSearchFilters,
  specialty: string,
): QuestionSearchFilters {
  return { ...f, specialty, theme: '' };
}

export function changeTheme(
  f: QuestionSearchFilters,
  theme: string,
): QuestionSearchFilters {
  return { ...f, theme };
}

export function changeUf(
  f: QuestionSearchFilters,
  uf: string,
): QuestionSearchFilters {
  return { ...f, uf, institutionId: '' };
}

export function changeInstitution(
  f: QuestionSearchFilters,
  institutionId: string,
): QuestionSearchFilters {
  return { ...f, institutionId };
}

export function changeYear(
  f: QuestionSearchFilters,
  year: string,
): QuestionSearchFilters {
  return { ...f, year };
}

/** O que a pessoa pode escolher, para conferir o que veio da URL. */
export interface QuestionFilterContext {
  /** Todas as instituições conhecidas, inclusive arquivadas; `undefined` carregando. */
  institutionsById?: Map<string, Institution>;
  /** Os ids de cada nível; `undefined` enquanto carrega, falhou ou não tem pai. */
  areaIds?: string[];
  specialtyIds?: string[];
  themeIds?: string[];
  years?: number[];
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Descarta em silêncio o que não pode estar ali: UF fora da lista, ano que não
 * é inteiro, id que não é UUID (o back daria 400), o nível sem o pai de que
 * depende, a instituição apagada ou de outra UF, e o assunto ou ano fora das
 * opções. O que ainda carrega não é julgado: só sai quando a resposta mostra
 * que é inválido.
 */
export function dropInvalidQuestionFilters(
  f: QuestionSearchFilters,
  ctx: QuestionFilterContext,
): QuestionSearchFilters {
  const next = { ...f };
  if (next.uf && !(UFS as readonly string[]).includes(next.uf)) next.uf = '';
  // O back aceita de 1900 a 2100; `0000` passaria na forma e daria 400.
  const year = Number(next.year);
  if (
    next.year &&
    !(/^\d{4}$/.test(next.year) && year >= 1900 && year <= 2100)
  ) {
    next.year = '';
  }
  for (const key of ['area', 'specialty', 'theme', 'institutionId'] as const) {
    if (next[key] && !UUID.test(next[key])) next[key] = '';
  }

  if (!next.uf) next.institutionId = '';
  if (next.institutionId && ctx.institutionsById) {
    const inst = ctx.institutionsById.get(next.institutionId);
    if (!inst || inst.deletedAt || inst.uf !== next.uf) next.institutionId = '';
  }

  if (next.area && ctx.areaIds && !ctx.areaIds.includes(next.area)) {
    next.area = '';
  }
  if (!next.area) next.specialty = '';
  if (
    next.specialty &&
    ctx.specialtyIds &&
    !ctx.specialtyIds.includes(next.specialty)
  ) {
    next.specialty = '';
  }
  if (!next.specialty) next.theme = '';
  if (next.theme && ctx.themeIds && !ctx.themeIds.includes(next.theme)) {
    next.theme = '';
  }

  if (next.year && ctx.years && !ctx.years.includes(Number(next.year))) {
    next.year = '';
  }
  return next;
}

export function sameQuestionFilters(
  a: QuestionSearchFilters,
  b: QuestionSearchFilters,
): boolean {
  return (
    Object.keys(EMPTY_QUESTION_FILTERS) as (keyof QuestionSearchFilters)[]
  ).every((key) => a[key] === b[key]);
}
